import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconsSend from '@mui/icons-material/Send';

import makeStyles from '@mui/styles/makeStyles';

import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { Theme } from '@mui/material/styles';
import useForm from '../../../../../components/_hook/useForm';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import useNotification from '../../../../../components/_hook/useNotification';
import { useBatchUpdatePriceProductMutation } from '../../../../../../graphql/mutation/product/batchUpdatePriceProductMutation';
import SelectCurrency from '../../../../../components/_select/SelectCurrency';
import { IValue } from '../../../../../components/_select/_module/Select';

const useStyles = makeStyles<Theme>((theme) => ({
  paperTabs: {
    flexGrow: 1,
    marginBottom: theme.spacing(2),
  },
}));

interface IProps {
  batchUpdatePriceProducts: {
    productId: string;
    variationId?: string;
  }[];
  onCompleted: () => void;
  onCancel: () => void;
}

export default function ProductDropshippingPriceManagementBatchUpdate(
  props: IProps
) {
  interface IFormValue {
    priceCurrency?: string;
    priceFinal?: number;
    dropshippingPriceCurrency?: string;
    dropshippingPriceFinal?: number;
    dropshippingPriceFinalByPricePercentage?: number;
  }

  const classes = useStyles();
  const { batchUpdatePriceProducts, onCancel, onCompleted } = props;
  const { notification } = useNotification();
  const { t } = useTranslation();

  const TABS = {
    SALES_PRICE: 'salesPrice',
    DROPSHIPPER: 'dropshipper',
  };
  const DROPSHIPPER_UPDATE_BY = {
    VALUE: 'value',
    PERCENTAGE: 'percentage',
  };
  const [currentTab, setCurrentTab] = useState(TABS.SALES_PRICE);
  const [dropshipperUpdateBy, setDropshipperUpdateBy] = useState(
    DROPSHIPPER_UPDATE_BY.VALUE
  );
  const [
    batchUpdatePriceProductMutation,
    { loading: isBatchUpdatingPriceProduct },
  ] = useBatchUpdatePriceProductMutation(
    `updatedProducts {
				_id
				prices {
					final
					currency
				}
				dropshipping {
                  prices {
                    currency
                    final
                  }
                }
				variations { 
                  id
                  attributes
                  dropshipping {
                    prices {
                      currency
                      final
                    }
                  }
                }
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.batchUpdatePriceProduct &&
          data.batchUpdatePriceProduct.updatedProducts
        ) {
          notification.success(
            t('__item___updated', {
              item: `${data.batchUpdatePriceProduct.updatedProducts.length} ${t(
                'product',
                {
                  count: data.batchUpdatePriceProduct.updatedProducts.length,
                }
              )}`,
            })
          );
        }
        onCompleted();
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onSubmit(tempValues: IFormValue) {
    const {
      priceCurrency,
      priceFinal,
      dropshippingPriceCurrency,
      dropshippingPriceFinal,
      dropshippingPriceFinalByPricePercentage,
    } = tempValues;

    let tempPriceCurrency: string | undefined;
    let tempPriceFinal: number | undefined;
    let tempDropshippingPriceCurrency: string | undefined;
    let tempDropshippingPriceFinal: number | undefined;
    let tempDropshippingPriceFinalByPricePercentage: number | undefined;
    if (currentTab === TABS.SALES_PRICE) {
      tempPriceCurrency = priceCurrency;
      tempPriceFinal = priceFinal;
    }

    if (currentTab === TABS.DROPSHIPPER) {
      tempDropshippingPriceCurrency = dropshippingPriceCurrency;
      if (dropshipperUpdateBy === DROPSHIPPER_UPDATE_BY.VALUE) {
        tempDropshippingPriceFinal = dropshippingPriceFinal;
      } else {
        tempDropshippingPriceFinalByPricePercentage = dropshippingPriceFinalByPricePercentage;
      }
    }

    await batchUpdatePriceProductMutation({
      variables: {
        batchUpdatePriceProducts: batchUpdatePriceProducts.map(
          (batchUpdatePriceProduct) => ({
            productId: batchUpdatePriceProduct.productId,
            variationId: batchUpdatePriceProduct.variationId,
            priceCurrency: tempPriceCurrency,
            priceFinal: tempPriceFinal,
            dropshippingPriceCurrency: tempDropshippingPriceCurrency,
            dropshippingPriceFinal: tempDropshippingPriceFinal,
            dropshippingPriceFinalByPricePercentage: tempDropshippingPriceFinalByPricePercentage,
          })
        ),
      },
    });
  }

  function onValidate(tempValues: IFormValue) {
    const newErrors: Record<string, string> = {};

    if (currentTab === TABS.SALES_PRICE) {
      if (!tempValues.priceCurrency) {
        newErrors.priceCurrency = t('__names__are_required', {
          names: t('currency'),
        });
      }

      if (!tempValues.priceFinal) {
        newErrors.priceFinal = t('__names__are_required', {
          names: t('price'),
        });
      }
    }

    if (currentTab === TABS.DROPSHIPPER) {
      if (dropshipperUpdateBy === DROPSHIPPER_UPDATE_BY.VALUE) {
        if (!tempValues.dropshippingPriceCurrency) {
          newErrors.dropshippingPriceCurrency = t('__names__are_required', {
            names: t('currency'),
          });
        }
        if (!tempValues.dropshippingPriceFinal) {
          newErrors.dropshippingPriceFinal = t('__names__are_required', {
            names: t('price'),
          });
        }
      }

      if (dropshipperUpdateBy === DROPSHIPPER_UPDATE_BY.PERCENTAGE) {
        if (!tempValues.dropshippingPriceFinalByPricePercentage) {
          newErrors.dropshippingPriceFinalByPricePercentage = t(
            '__names__are_required',
            {
              names: t('percentage'),
            }
          );
        } else if (tempValues.dropshippingPriceFinalByPricePercentage < 0) {
          newErrors.dropshippingPriceFinalByPricePercentage = t(
            '__names__must_greater_than__number__',
            {
              names: t('percentage'),
              number: 0,
            }
          );
        }
      }
    }

    return newErrors;
  }

  const {
    values,
    handleSubmit,
    errors,
    handleValidation,
    setValue,
  } = useForm<IFormValue>({
    defaultValues: {
      priceCurrency: undefined,
      priceFinal: undefined,
      dropshippingPriceCurrency: undefined,
      dropshippingPriceFinal: undefined,
      dropshippingPriceFinalByPricePercentage: undefined,
    },
    onSubmit,
    onValidate,
  });

  return (
    <>
      <DialogTitle style={{ textTransform: 'capitalize' }}>
        {t('batch_update')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} justifyContent="center">
          <Grid container justifyContent="center" direction="row" xs={10}>
            <Paper className={classes.paperTabs}>
              <Tabs
                variant="fullWidth"
                centered
                value={currentTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={(event, newValue) => setCurrentTab(newValue)}
              >
                <Tab label={t('current_sales_price')} value="salesPrice" />
                <Tab
                  label={t('sales_price_to_dropshipper')}
                  value="dropshipper"
                />
              </Tabs>
            </Paper>
            <Fade in={currentTab === TABS.SALES_PRICE}>
              <Grid
                container
                spacing={2}
                item
                xs={12}
                style={{
                  display: currentTab === TABS.SALES_PRICE ? 'inherit' : 'none',
                }}
              >
                <Grid item xs={12}>
                  <SelectCurrency<IValue>
                    isClearable
                    menuPosition="fixed"
                    useMui
                    fullWidth
                    required
                    queryVariables={{
                      filter: {},
                    }}
                    label={t('currency')}
                    value={values.priceCurrency}
                    onChange={(option) => {
                      setValue(
                        'priceCurrency',
                        option ? option.value : undefined
                      );
                    }}
                    error={Boolean(errors.priceCurrency)}
                    helperText={errors.priceCurrency}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type="number"
                    fullWidth
                    required
                    label={t('selling_price')}
                    value={values.priceFinal}
                    onChange={(e) => {
                      setValue('priceFinal', parseFloat(e.target.value));
                    }}
                    error={Boolean(errors.priceFinal)}
                    helperText={errors.priceFinal}
                    onBlur={handleValidation}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {values.priceCurrency}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Fade>
            <Fade in={currentTab === TABS.DROPSHIPPER}>
              <Grid
                container
                spacing={2}
                item
                xs={12}
                style={{
                  display: currentTab === TABS.DROPSHIPPER ? 'inherit' : 'none',
                }}
              >
                <Grid item xs={12}>
                  <FormControl>
                    <FormLabel>{t('update_by')}</FormLabel>
                    <RadioGroup
                      row
                      value={dropshipperUpdateBy}
                      onChange={(e) => setDropshipperUpdateBy(e.target.value)}
                    >
                      <FormControlLabel
                        value={DROPSHIPPER_UPDATE_BY.VALUE}
                        control={<Radio color="primary" />}
                        label={t('value')}
                      />
                      <FormControlLabel
                        value={DROPSHIPPER_UPDATE_BY.PERCENTAGE}
                        control={<Radio color="primary" />}
                        label={t('percentage_based_on_current_sales_price')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {dropshipperUpdateBy === DROPSHIPPER_UPDATE_BY.VALUE && (
                  <>
                    <Grid item xs={12}>
                      <SelectCurrency<IValue>
                        isClearable
                        menuPosition="fixed"
                        useMui
                        fullWidth
                        required
                        queryVariables={{
                          filter: {},
                        }}
                        label={t('currency')}
                        value={values.dropshippingPriceCurrency}
                        onChange={(option) => {
                          setValue(
                            'dropshippingPriceCurrency',
                            option ? option.value : undefined
                          );
                        }}
                        error={Boolean(errors.dropshippingPriceCurrency)}
                        helperText={errors.dropshippingPriceCurrency}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        type="number"
                        fullWidth
                        required
                        label={t('selling_price')}
                        value={values.dropshippingPriceFinal}
                        onChange={(e) => {
                          setValue(
                            'dropshippingPriceFinal',
                            parseFloat(e.target.value)
                          );
                        }}
                        error={Boolean(errors.dropshippingPriceFinal)}
                        helperText={errors.dropshippingPriceFinal}
                        onBlur={handleValidation}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {values.priceCurrency}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                {dropshipperUpdateBy === DROPSHIPPER_UPDATE_BY.PERCENTAGE && (
                  <>
                    <Grid item xs={12}>
                      <SelectCurrency<IValue>
                        useMui
                        fullWidth
                        disabled
                        onChange={() => {}}
                        placeholder={t(
                          'currency_will_auto_update_based_on_current_sales_price_currency'
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        type="number"
                        fullWidth
                        required
                        label={t('percentage_based_on_current_sales_price')}
                        value={values.dropshippingPriceFinalByPricePercentage}
                        onChange={(e) => {
                          setValue(
                            'dropshippingPriceFinalByPricePercentage',
                            parseFloat(e.target.value)
                          );
                        }}
                        error={Boolean(
                          errors.dropshippingPriceFinalByPricePercentage
                        )}
                        helperText={
                          errors.dropshippingPriceFinalByPricePercentage
                        }
                        onBlur={handleValidation}
                        inputProps={{
                          min: '0',
                          step: '1',
                        }}
                        // eslint-disable-next-line react/jsx-no-duplicate-props
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Fade>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={isBatchUpdatingPriceProduct}
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
